import type { RuleGroupType, RuleType } from "react-querybuilder";
import {
  normalizePlanTypeApiCode,
  type AutoTaggingPlanDimensionItemWrite,
} from "../../../services/autoTaggingService";
import { buildReferenceItemsFromObjectSubType } from "../TaggingPlan/classificationPlanApiMapping";
import type { EnhancedTaggingRuleObjectSelection } from "./EnhancedTaggingRuleObjectStep";
import {
  createEmptyConditionValue,
  parseEnhancedTaggingConditionValue,
  type EnhancedTaggingConditionValue,
} from "./enhancedTaggingConditionValue";
import {
  canonicalCountOperator,
  countCodeFromField,
  countNumberFromRuleValue,
  isCountRule,
  normalizeCountField,
} from "./countCondition";
import type { EnhancedTaggingThenTag } from "./EnhancedTaggingRuleThenStep";

/** Leaf rule shape expected by POST /auto-tagging/conditional-plans. */
export type ConditionalPlanRuleValueItem = {
  code: string;
  name: string;
};

export type ConditionalPlanConceptRulePayload = {
  type: "concept";
  field: string;
  operator: string;
  value: Array<string | ConditionalPlanRuleValueItem>;
  valueSource: "value";
};

export type ConditionalPlanCountRulePayload = {
  type: "count";
  field: string;
  operator: string;
  value: number;
};

export type ConditionalPlanRulePayload =
  | ConditionalPlanConceptRulePayload
  | ConditionalPlanCountRulePayload;

/** Nested condition group for conditional plans. */
export type ConditionalPlanConditionPayload = {
  combinator: string;
  rules: Array<ConditionalPlanRulePayload | ConditionalPlanConditionPayload>;
  not: boolean;
};

export type ConditionalPlanCreatePayload = {
  /** Assessment Name — required on create; sent on update when non-empty. */
  name: string;
  target_type: string;
  /**
   * Reference Dimension filters, same shape as Classification `reference_items`.
   * Selected value codes are not sent as `sub_type`.
   */
  reference_items: AutoTaggingPlanDimensionItemWrite[];
  condition: ConditionalPlanConditionPayload;
  concept: string;
  concept_value: {
    name: string;
    code: string;
    description: string;
  };
};

function isRuleGroup(item: unknown): item is RuleGroupType {
  return Boolean(item && typeof item === "object" && "rules" in item);
}

function mapApiValueToConditionValue(value: unknown): EnhancedTaggingConditionValue {
  if (Array.isArray(value)) {
    const conceptValues = value
      .map((item) => {
        if (item && typeof item === "object") {
          const record = item as Record<string, unknown>;
          const code = String(record.code ?? record.value ?? "").trim();
          if (!code) return null;
          const name =
            String(record.name ?? record.label ?? record.display_name ?? "").trim() ||
            code;
          return { id: code, code, name };
        }
        const code = String(item ?? "").trim();
        if (!code) return null;
        return { id: code, code, name: code };
      })
      .filter((item): item is { id: string; code: string; name: string } => item !== null);

    if (conceptValues.length === 1 && conceptValues[0].code === "*") {
      return { allValues: true, conceptValues: [] };
    }
    return {
      allValues: false,
      conceptValues,
    };
  }

  return parseEnhancedTaggingConditionValue(value);
}

function mapConditionRule(rule: RuleType): ConditionalPlanRulePayload {
  if (isCountRule(rule)) {
    return {
      type: "count",
      field: countCodeFromField(normalizeCountField(String(rule.field ?? ""))),
      operator: canonicalCountOperator(String(rule.operator ?? "")),
      value: countNumberFromRuleValue(rule.value),
    };
  }

  const parsed = parseEnhancedTaggingConditionValue(rule.value);
  const value = parsed.allValues
    ? ["*"]
    : parsed.conceptValues
        .map((item) => {
          const code = item.code.trim();
          if (!code) return null;
          return {
            code,
            // Persist display name so list Condition Description can show names.
            name: item.name.trim() || code,
          };
        })
        .filter((item): item is ConditionalPlanRuleValueItem => item !== null);

  return {
    type: "concept",
    field: String(rule.field ?? "").trim(),
    operator: String(rule.operator ?? "").trim(),
    value,
    valueSource: "value",
  };
}

export function mapConditionQueryToPayload(
  query: RuleGroupType,
): ConditionalPlanConditionPayload {
  return {
    combinator: String(query.combinator ?? "and"),
    not: Boolean(query.not),
    rules: query.rules.map((item) => {
      if (isRuleGroup(item)) {
        return mapConditionQueryToPayload(item);
      }
      return mapConditionRule(item as RuleType);
    }),
  };
}

/** Hydrate the condition builder from an API condition payload. */
export function mapConditionPayloadToEditorQuery(
  condition: unknown,
): RuleGroupType {
  if (!isRuleGroup(condition) || !Array.isArray(condition.rules)) {
    return {
      combinator: "or",
      rules: [
        {
          combinator: "and",
          rules: [
            {
              field: "",
              operator: "containsAny",
              value: createEmptyConditionValue(),
            },
          ],
        },
      ],
    };
  }

  const mapGroup = (group: RuleGroupType): RuleGroupType => ({
    combinator: String(group.combinator ?? "and"),
    not: Boolean(group.not),
    rules: (group.rules ?? []).map((item) => {
      if (isRuleGroup(item)) {
        return mapGroup(item);
      }
      const rule = item as RuleType & { type?: unknown };
      if (isCountRule(rule)) {
        const raw = String(rule.field ?? "").trim();
        const code = countCodeFromField(raw) || raw;
        return {
          field: code ? `count:${code}` : "count:",
          operator: canonicalCountOperator(String(rule.operator ?? "")),
          value: countNumberFromRuleValue(rule.value),
          valueSource: "value" as const,
          ruleType: "count" as const,
        };
      }
      return {
        field: String(rule.field ?? "").trim(),
        operator: String(rule.operator ?? "containsAny").trim() || "containsAny",
        value: mapApiValueToConditionValue(rule.value),
        valueSource: "value" as const,
      };
    }),
  });

  return mapGroup(condition);
}

export function buildConditionalPlanCreatePayload(
  conditionQuery: RuleGroupType,
  thenTag: EnhancedTaggingThenTag,
  objectSelection?: Partial<EnhancedTaggingRuleObjectSelection>,
  assessmentName = "",
): ConditionalPlanCreatePayload {
  return {
    name: assessmentName.trim(),
    target_type: normalizePlanTypeApiCode(objectSelection?.objectType ?? ""),
    reference_items: buildReferenceItemsFromObjectSubType({
      objectSubType: objectSelection?.objectSubType ?? "",
      objectSubTypeName: objectSelection?.objectSubTypeName,
      referenceConceptCode: objectSelection?.referenceConceptCode,
      referenceDimensions: objectSelection?.referenceDimensions,
    }),
    condition: mapConditionQueryToPayload(conditionQuery),
    concept: thenTag.dimensionCode.trim(),
    concept_value: {
      name: thenTag.valueLabel.trim() || thenTag.valueCode.trim(),
      code: thenTag.valueCode.trim(),
      description: "",
    },
  };
}
